import React, { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
}

export class ChunkErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(_: Error): State {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Chunk Error caught:", error, errorInfo);
    
    // If the error is related to Vite chunk loading or dynamic imports failing
    if (
      error.message &&
      (error.message.includes("Failed to fetch dynamically imported module") ||
       error.message.includes("Importing a module script failed") ||
       error.name === "ChunkLoadError")
    ) {
      // Check if we've already reloaded to prevent an infinite loop
      const isReloaded = sessionStorage.getItem("chunk-reloaded");
      if (!isReloaded) {
        sessionStorage.setItem("chunk-reloaded", "true");
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('t', Date.now().toString());
        window.location.href = currentUrl.toString();
      }
    }
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[50vh] flex flex-col items-center justify-center p-8 text-center animate-in fade-in duration-500">
          <h2 className="text-2xl font-bold mb-4">A new version is available</h2>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            We couldn't load parts of the page because a new update was just deployed. Please refresh to get the latest version.
          </p>
          <button
            onClick={() => {
              sessionStorage.removeItem("chunk-reloaded");
              const currentUrl = new URL(window.location.href);
              currentUrl.searchParams.set('t', Date.now().toString());
              window.location.href = currentUrl.toString();
            }}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-colors font-medium shadow-md"
          >
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
